import cloneDeep from "lodash/cloneDeep"
import { useCallback, useReducer, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Classes,
  Icon,
  Intent,
  Tooltip,
  Tree,
  TreeNodeInfo,
} from "@blueprintjs/core"
import { Dashboard } from "../types/dashboard"

type NodePath = number[]

type TreeAction =
  | { type: "SET_IS_EXPANDED"; payload: { path: NodePath; isExpanded: boolean } }
  | { type: "DESELECT_ALL" }
  | { type: "SET_IS_SELECTED"; payload: { path: NodePath; isSelected: boolean } }

function forEachNode(nodes: TreeNodeInfo[] | undefined, callback: (node: TreeNodeInfo) => void) {
  if (nodes === undefined) {
    return
  }
  for (const node of nodes) {
    callback(node)
    forEachNode(node.childNodes, callback)
  }
}

function forNodeAtPath(
  nodes: TreeNodeInfo[],
  path: NodePath,
  callback: (node: TreeNodeInfo) => void,
) {
  callback(Tree.nodeFromPath(path, nodes))
}

function treeReducer(state: TreeNodeInfo[], action: TreeAction) {
  switch (action.type) {
    case "DESELECT_ALL": {
      const newState1 = cloneDeep(state)
      forEachNode(newState1, node => (node.isSelected = false))
      return newState1
    }
    case "SET_IS_EXPANDED": {
      const newState2 = cloneDeep(state)
      forNodeAtPath(
        newState2,
        action.payload.path,
        node => (node.isExpanded = action.payload.isExpanded),
      )
      return newState2
    }
    case "SET_IS_SELECTED": {
      const newState3 = cloneDeep(state)
      forNodeAtPath(
        newState3,
        action.payload.path,
        node => (node.isSelected = action.payload.isSelected),
      )
      return newState3
    }
    default:
      return state
  }
}

interface Props {
  dashboards: Dashboard[]
  currentDashboard: Dashboard | null
  onSelect: (dashboard: Dashboard) => void
  onDelete: (dashboardId: number) => void
  currentUsername: string
  onClose: () => void
}

export const DashboardTreeView = ({
  dashboards,
  currentDashboard,
  onSelect,
  onDelete,
  currentUsername,
  onClose,
}: Props) => {
  const [deleteId, setDeleteId] = useState<number | null>(null)

  // Build tree structure
  const buildTreeNodes = useCallback(
    (dashboardList: Dashboard[]): TreeNodeInfo[] => {
      // Group by owner
      const groups: Record<string, Dashboard[]> = {}
      dashboardList.forEach(dashboard => {
        const owner = dashboard.owner.username
        if (!groups[owner]) {
          groups[owner] = []
        }
        groups[owner].push(dashboard)
      })

      // Sort owners (current user first)
      const owners = Object.keys(groups).sort((a, b) => {
        if (a === currentUsername) return -1
        if (b === currentUsername) return 1
        return a.localeCompare(b)
      })

      // Build tree nodes
      return owners.map((owner, ownerIndex) => {
        const userDashboards = groups[owner]
        const isCurrentUser = owner === currentUsername

        // Check if this group contains the currently selected dashboard
        const containsSelectedDashboard = userDashboards.some(d => d.id === currentDashboard?.id)

        return {
          id: `owner-${ownerIndex}`,
          hasCaret: true,
          icon: "folder-close",
          isExpanded: containsSelectedDashboard, // Expand if this group has the selected dashboard
          label: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Icon icon="person" size={14} />
              <span style={{ fontWeight: 600 }}>
                {isCurrentUser ? "My Dashboards" : owner} ({userDashboards.length})
              </span>
            </div>
          ),
          childNodes: userDashboards.map((dashboard, dashIndex) => ({
            id: `dashboard-${ownerIndex}-${dashIndex}`,
            icon: "chart",
            isSelected: currentDashboard?.id === dashboard.id,
            label: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span style={{ flex: 1 }}>{dashboard.name}</span>
                {isCurrentUser && (
                  <Tooltip content="Delete dashboard">
                    <Button
                      minimal
                      small
                      icon="trash"
                      intent={Intent.DANGER}
                      onClick={e => {
                        e.stopPropagation()
                        setDeleteId(dashboard.id)
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            ),
            secondaryLabel: (
              <Tooltip content={`Updated: ${new Date(dashboard.updated_at).toLocaleString()}`}>
                <Icon icon="time" size={12} />
              </Tooltip>
            ),
            nodeData: dashboard,
          })),
        }
      })
    },
    [currentDashboard, currentUsername],
  )

  const initialNodes = useMemo(() => buildTreeNodes(dashboards), [dashboards, buildTreeNodes])

  const [nodes, dispatch] = useReducer(treeReducer, initialNodes)

  const handleNodeClick = useCallback(
    (node: TreeNodeInfo, nodePath: NodePath, e: React.MouseEvent<HTMLElement>) => {
      // If it's a folder node (has children), toggle expansion
      if (node.childNodes && node.childNodes.length > 0) {
        dispatch({
          type: "SET_IS_EXPANDED",
          payload: { path: nodePath, isExpanded: !node.isExpanded },
        })
      }
      // If it's a dashboard node, select it
      else if (node.nodeData) {
        const dashboard = node.nodeData as Dashboard
        onSelect(dashboard)

        // Deselect all first
        dispatch({ type: "DESELECT_ALL" })
        // Then select this one
        dispatch({
          type: "SET_IS_SELECTED",
          payload: { path: nodePath, isSelected: true },
        })
      }
    },
    [onSelect],
  )

  const handleNodeCollapse = useCallback((_node: TreeNodeInfo, nodePath: NodePath) => {
    dispatch({
      type: "SET_IS_EXPANDED",
      payload: { path: nodePath, isExpanded: false },
    })
  }, [])

  const handleNodeExpand = useCallback((_node: TreeNodeInfo, nodePath: NodePath) => {
    dispatch({
      type: "SET_IS_EXPANDED",
      payload: { path: nodePath, isExpanded: true },
    })
  }, [])

  const handleDelete = () => {
    if (deleteId) {
      // Check if we're deleting the currently selected dashboard
      const isDeletingCurrentDashboard = currentDashboard?.id === deleteId

      // Find the next dashboard to select
      let nextDashboard: Dashboard | null = null
      if (isDeletingCurrentDashboard) {
        const currentIndex = dashboards.findIndex(d => d.id === deleteId)
        if (currentIndex !== -1) {
          // Try to select the next dashboard, or the previous one if this is the last
          if (currentIndex < dashboards.length - 1) {
            nextDashboard = dashboards[currentIndex + 1]
          } else if (currentIndex > 0) {
            nextDashboard = dashboards[currentIndex - 1]
          }
        }
      }

      // Delete the dashboard
      onDelete(deleteId)

      // Select the next dashboard if we deleted the current one
      if (isDeletingCurrentDashboard && nextDashboard) {
        onSelect(nextDashboard)
      }

      // Close the drawer
      onClose()

      setDeleteId(null)
    }
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Tree
          contents={nodes}
          onNodeClick={handleNodeClick}
          onNodeCollapse={handleNodeCollapse}
          onNodeExpand={handleNodeExpand}
          className={Classes.ELEVATION_0}
        />
      </div>

      {/* Delete Confirmation Alert */}
      <Alert
        isOpen={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        intent={Intent.DANGER}
        icon="trash"
      >
        <p>Are you sure you want to delete this dashboard? This action cannot be undone.</p>
      </Alert>
    </>
  )
}
